import { assign } from 'xstate';
import { LeaseDocuments } from '../../gql/graphql';
import { getCurrentLanguage, SupportedLanguage } from '../../helpers/language/language.helper';

/**
 * Shared by the LeaseAgreement sub-machines in the long form, short form and virtual card flows.
 *
 * The render language is not a GraphQL argument: Apollo puts the currently-selected UI language
 * on the X-Language header, read at call time (src/apollo/index.ts), and the backend picks the
 * matching template. Re-invoking generateContract after a toggle is all it takes.
 */

type LeaseDocumentsByLanguage = Partial<Record<SupportedLanguage, LeaseDocuments>>;

/**
 * The cache is dropped on every entry rather than kept for the session: re-entering the page
 * means the lease terms may have been re-selected (e.g. EDIT_CART), which would make an earlier
 * render stale. Toggling language moves between child states, so it does not re-run this.
 */
export const AssignInitialLeaseDocumentLanguage = assign((_context: any, _event: any) => ({
  leaseDocumentsLanguage: getCurrentLanguage(),
  leaseDocumentsByLanguage: {} as LeaseDocumentsByLanguage,
}));

export const AssignGeneratedLeaseDocuments = assign((context: any, event: any) => {
  const leaseDocuments: LeaseDocuments | undefined = event.data?.leaseDocuments;
  // Files under the language the request went out in, not the one on screen now, so a toggle
  // landing mid-flight cannot mislabel the response.
  const language: SupportedLanguage = context.leaseDocumentsLanguage ?? getCurrentLanguage();

  return {
    leaseDocuments,
    leaseDocumentsByLanguage: {
      ...context.leaseDocumentsByLanguage,
      [language]: leaseDocuments,
    } as LeaseDocumentsByLanguage,
  };
});

/**
 * Neither machine ever resets these — every write is an event payload — which was harmless while
 * generation ran once per entry. Now that a toggle can re-run it, a failure in one language would
 * otherwise strand the page on InternalError for good: the toggle lives in the footer and stays
 * clickable, so the customer would keep regenerating behind an error screen they can never leave.
 */
const clearedErrors = { generateContractErrors: [], signingErrors: [] };

/**
 * Register on both `waiting` and `callingGenerateContract` — on the latter so a second toggle
 * mid-request re-enters the state, cancelling the in-flight call. An event without a language
 * matches neither branch and is ignored.
 */
export const createLanguageChangedTransitions = () => [
  {
    cond: (context: any, event: any) => !!context.leaseDocumentsByLanguage?.[event.data?.language as SupportedLanguage],
    target: 'waiting',
    actions: assign((context: any, event: any) => ({
      leaseDocumentsLanguage: event.data?.language,
      leaseDocuments: context.leaseDocumentsByLanguage?.[event.data?.language as SupportedLanguage],
      ...clearedErrors,
    })),
  },
  {
    cond: (_context: any, event: any) => !!event.data?.language,
    target: 'callingGenerateContract',
    actions: assign((_context: any, event: any) => ({
      leaseDocumentsLanguage: event.data?.language,
      ...clearedErrors,
    })),
  },
];
